// Password reset attempts — written by the requestPasswordReset Cloud Function
// (rate-limited, logged). Admins see who is requesting resets, filter by
// email, and block abusive IPs (blockedIps/{ipKey}).
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { PageHeader, Table, Badge, StatCard, GoldBtn, SearchInput, Spinner } from '../components/UI';

const REASON_LABEL = {
  ok: 'Sent', no_account: 'No account (silent)', email_limit: 'Email limit hit',
  ip_limit: 'IP limit hit', blocked_ip: 'Blocked IP', invalid_email: 'Invalid email', send_failed: 'Send failed',
};
const REASON_COLOR = {
  ok: 'reviewed', no_account: 'dismissed', email_limit: 'flagged', ip_limit: 'flagged',
  blocked_ip: 'open', invalid_email: 'dismissed', send_failed: 'open',
};
const ipKey = ip => String(ip || '').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 64);

export default function ResetAbuse() {
  const [attempts, setAttempts] = useState([]);
  const [blocked,  setBlocked]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        getDocs(query(collection(db, 'resetAttempts'), orderBy('at', 'desc'), limit(300))),
        getDocs(collection(db, 'blockedIps')),
      ]);
      setAttempts(a.docs.map(d => ({ id: d.id, ...d.data() })));
      setBlocked(b.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); alert('Could not load: ' + e.message); }
    setLoading(false);
  }

  async function blockIp(ip) {
    if (!ip || ip === 'unknown') return;
    if (!window.confirm(`Block ${ip}? Reset requests from this IP will send nothing.\n(Mobile IPs change, so the rate limits matter more.)`)) return;
    try {
      await setDoc(doc(db, 'blockedIps', ipKey(ip)), { ip, blockedAt: serverTimestamp(), by: auth.currentUser?.email || '' });
      load();
    } catch (e) { alert('Error: ' + e.message); }
  }

  async function unblock(id) {
    try { await deleteDoc(doc(db, 'blockedIps', id)); load(); } catch (e) { alert('Error: ' + e.message); }
  }

  const blockedSet = useMemo(() => new Set(blocked.map(b => b.id)), [blocked]);
  const shown = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? attempts.filter(a => (a.email || '').includes(s) || (a.ip || '').includes(s)) : attempts;
  }, [attempts, search]);

  const today = new Date().toDateString();
  const todayRows = attempts.filter(a => a.at?.toDate && a.at.toDate().toDateString() === today);
  const stats = {
    today: todayRows.length,
    stopped: todayRows.filter(a => ['email_limit', 'ip_limit', 'blocked_ip'].includes(a.reason)).length,
    sent: todayRows.filter(a => a.sent).length,
    ips: new Set(todayRows.map(a => a.ip)).size,
  };

  const fmt = ts => ts?.toDate
    ? ts.toDate().toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '—';

  const COLUMNS = [
    { key: 'at', label: 'Time', render: fmt },
    { key: 'email', label: 'Email', render: v => <span style={{ fontSize: 12.5 }}>{v || '—'}</span> },
    { key: 'ip', label: 'IP', render: v => (
        <span style={{ fontFamily: 'DM Mono', fontSize: 12 }}>{v}{blockedSet.has(ipKey(v)) ? ' 🚫' : ''}</span>) },
    { key: 'reason', label: 'Outcome', render: v => <Badge label={REASON_LABEL[v] || v} color={REASON_COLOR[v] || 'open'} /> },
    { key: 'appVerified', label: 'Genuine app', render: v => v ? '✅' : '—' },
    { key: 'platform', label: 'From', render: (v, r) => (
        <span title={r.userAgent} style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)' }}>{v || '?'} · {(r.userAgent || '').slice(0, 28)}</span>) },
    { key: 'id', label: '', render: (v, r) => (
        <div onClick={e => e.stopPropagation()}>
          {!blockedSet.has(ipKey(r.ip)) && r.ip !== 'unknown' &&
            <GoldBtn small danger onClick={() => blockIp(r.ip)}>Block IP</GoldBtn>}
        </div>) },
  ];

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Password Resets"
        subtitle="Every Forgot Password request (max 2 emails per address and 5 per IP per day). Block abusive IPs here." />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Requests Today" value={stats.today} icon="🔐" color="#D4AF37" borderColor="#D4AF37" />
        <StatCard label="Stopped Today" value={stats.stopped} icon="🛑" color="#ef4444" borderColor="#ef4444" sub="Limit hit or blocked IP" />
        <StatCard label="Emails Sent Today" value={stats.sent} icon="✉️" color="#008751" borderColor="#008751" />
        <StatCard label="Distinct IPs Today" value={stats.ips} icon="🌐" color="#a855f7" borderColor="#a855f7" />
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <div style={{ flex: 1 }}><SearchInput value={search} onChange={setSearch} placeholder="Filter by email or IP…" /></div>
        <GoldBtn onClick={load}>Refresh</GoldBtn>
      </div>

      <div style={{ background: '#1A2332', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, overflow: 'hidden', marginBottom: 24 }}>
        {loading && attempts.length === 0 ? <Spinner /> : <Table columns={COLUMNS} data={shown} loading={false} />}
      </div>

      <h3 style={{ fontFamily: 'Syne', fontSize: 16, margin: '8px 0 12px' }}>Blocked IPs ({blocked.length})</h3>
      {blocked.length === 0
        ? <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>None yet.</div>
        : blocked.map(b => (
            <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px',
              background: '#0F1419', borderRadius: 10, marginBottom: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontFamily: 'DM Mono', fontSize: 13 }}>{b.ip || b.id}</span>
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{fmt(b.blockedAt)} {b.by ? `· ${b.by}` : ''}</span>
              <GoldBtn small onClick={() => unblock(b.id)}>Unblock</GoldBtn>
            </div>))}
    </div>
  );
}
